import { useState, useCallback } from 'react'

/**
 * Configuration for a confirmation modal
 */
export interface ConfirmationModalConfig {
  title: string
  description: string
  confirmText?: string
  confirmVariant?: 'default' | 'destructive'
}

/**
 * Return type for useConfirmationModals hook
 */
export interface UseConfirmationModalsReturn {
  /**
   * ID of the currently active modal (null if none)
   */
  activeModal: string | null
  /**
   * Check if a specific modal is currently open
   */
  isModalOpen: (modalId: string) => boolean
  /**
   * Open a modal by ID
   */
  openModal: (modalId: string) => void
  /**
   * Close the currently active modal
   */
  closeModal: () => void
  /**
   * Get props to spread onto a ConfirmationModal component
   */
  getModalProps: (
    modalId: string,
    onConfirm: () => void,
    config: ConfirmationModalConfig
  ) => {
    isOpen: boolean
    onClose: () => void
    onConfirm: () => void
    title: string
    description: string
    confirmText: string
    confirmVariant: 'default' | 'destructive'
  }
}

/**
 * Custom hook for managing multiple confirmation modals on a single page.
 * Ensures only one modal is open at a time and provides a consistent API.
 *
 * @returns Object with modal state and management functions
 *
 * @example
 * ```tsx
 * const modals = useConfirmationModals()
 *
 * // In your JSX:
 * <Button onClick={() => modals.openModal('delete')}>Delete</Button>
 * <Button onClick={() => modals.openModal('cancel')}>Cancel</Button>
 *
 * // Render modals:
 * <ConfirmationModal
 *   {...modals.getModalProps('delete', handleDelete, {
 *     title: 'Delete Item?',
 *     description: 'This action cannot be undone.',
 *     confirmText: 'Delete',
 *     confirmVariant: 'destructive',
 *   })}
 * />
 *
 * <ConfirmationModal
 *   {...modals.getModalProps('cancel', handleCancel, {
 *     title: 'Discard Changes?',
 *     description: 'You have unsaved changes.',
 *     confirmText: 'Discard',
 *     confirmVariant: 'destructive',
 *   })}
 * />
 * ```
 */
export function useConfirmationModals(): UseConfirmationModalsReturn {
  const [activeModal, setActiveModal] = useState<string | null>(null)

  const isModalOpen = useCallback(
    (modalId: string) => {
      return activeModal === modalId
    },
    [activeModal]
  )

  const openModal = useCallback((modalId: string) => {
    setActiveModal(modalId)
  }, [])

  const closeModal = useCallback(() => {
    setActiveModal(null)
  }, [])

  const getModalProps = useCallback(
    (
      modalId: string,
      onConfirm: () => void,
      config: ConfirmationModalConfig
    ) => {
      const handleConfirm = () => {
        onConfirm()
        closeModal()
      }

      return {
        isOpen: activeModal === modalId,
        onClose: closeModal,
        onConfirm: handleConfirm,
        title: config.title,
        description: config.description,
        confirmText: config.confirmText || 'Confirm',
        confirmVariant: config.confirmVariant || 'default',
      }
    },
    [activeModal, closeModal]
  )

  return {
    activeModal,
    isModalOpen,
    openModal,
    closeModal,
    getModalProps,
  }
}
